<template>
  <div
    class="pc-wrap"
    :class="{
      'pc-wrap--claimed': hasClaimedTrial && !isServiceExpiredLayout,
      'pc-wrap--expired': isServiceExpiredLayout,
    }"
  >
    <PcSidebar
      :active-menu="activeMenu"
      :user-name="userName"
      @change-menu="onMenuChange"
      @go-realname="openRealnameDialog"
      @realname-record="onRealnameRecord"
      @purchase="onPurchase"
      @apply-trial="onApplyTrial"
    />
    <main class="pc-main">
      <!-- 子路由渲染右侧面板，ref 供订单刷新等逻辑使用 -->
      <router-view ref="activePanel" @pay="onOrderPay" />
    </main>

    <RealnameDialog
      :visible.sync="showRealnameDialog"
      @success="onRealnameSuccess"
    />
    <RealnameRecordDialog :visible.sync="showRealnameRecordDialog" />

    <TrialAgreementDialog
      :visible.sync="showTrialAgreementDialog"
      :submitting="trialClaiming"
      @agree="onTrialAgreementAgree"
    />
    <TrialSuccessDialog
      :visible.sync="showTrialSuccessDialog"
      :trial-days="successTrialDays"
      @confirm="onTrialSuccessConfirm"
      @verify="onTrialSuccessOpenRealname"
      @purchase="onTrialSuccessPurchase"
    />

    <PlanSelectDialog
      :visible.sync="showPlanDialog"
      :verified="isPurchaseVerified(purchaseRealname)"
      :show-trial-tip="purchaseTrialTip"
      @confirm="onPlanConfirm"
      @realname="onPurchaseRealname"
    />
    <PurchaseAgreementDialog
      :visible.sync="showPurchaseAgreement"
      :submitting="purchasing"
      @agree="onPurchaseAgreementAgree"
    />
    <PayQrDialog
      :visible.sync="showPayQrDialog"
      :order-no="payOrderNo"
      :amount="payAmount"
      :show-trial-tip="purchaseTrialTip"
      @paid="onPurchasePaid"
    />
    <PurchaseSuccessDialog
      :visible.sync="showPurchaseSuccessDialog"
      :show-trial-tip="purchaseSuccessTrialTip"
      :show-renew-tip="purchaseSuccessRenewTip"
      @confirm="onPurchaseSuccessConfirm"
    />
  </div>
</template>

<script>
/**
 * 个人中心主布局：左侧菜单 + 右侧子路由面板 + 弹窗
 * 订阅、实名、试用、购买等弹窗在此统一挂载
 */
import '@/assets/icon1/iconfont.css';
import realnameDialogMixin from '@/view/user/realname/realnameDialogMixin';
import subscriptionTrialApplyMixin from '@/view/user/subscriptionService/subscriptionTrialApplyMixin';
import purchaseFlowMixin from '@/view/user/purchaseService/purchaseFlowMixin';
import RealnameRecordDialog from '@/view/user/realname/RealnameRecordDialog.vue';
import {
  refreshIndivSession,
  subscriptionState,
  realnameState,
} from '@/view/user/indivSession/indivSessionService';
import { personalCenterMenuPath } from '../routes';
import PcSidebar from '../sidebar/index.vue';

export default {
  name: 'PersonalCenterMain',
  mixins: [realnameDialogMixin, subscriptionTrialApplyMixin, purchaseFlowMixin],
  components: {
    PcSidebar,
    RealnameRecordDialog,
  },
  data() {
    return {
      showRealnameRecordDialog: false,
    };
  },
  computed: {
    /** 当前子路由对应左侧菜单高亮 */
    activeMenu() {
      return (this.$route.meta && this.$route.meta.menuKey) || 'basic';
    },
    /** 登录账号显示名，取自 vuex usermsg */
    userName() {
      const u = this.$store.state.usermsg;
      if (u && typeof u === 'object') {
        return u.name || u.userName || u.loginName || '';
      }
      return '';
    },
    /** 当前实名状态，供购买弹窗等使用 */
    purchaseRealname() {
      return realnameState.status;
    },
    /** 已领取试用或开通服务（hasService=true，用于金色边框 / 非过期布局） */
    hasClaimedTrial() {
      const s = subscriptionState.status;
      return !!(s && s.hasService);
    },
    /** 订阅 UI 视图（subscriptionService.buildView） */
    view() {
      return subscriptionState.view;
    },
    sideMode() {
      return this.view.mode;
    },
    isServiceExpiredLayout() {
      return this.view.grayLayout;
    },
  },
  mounted() {
    this.ensureIndivSession();
    this.ensurePanelRoute();
  },
  methods: {
    /** 兜底：未匹配到面板子路由时跳转基本信息 */
    ensurePanelRoute() {
      if (this.$route.meta && this.$route.meta.menuKey) return;
      this.$router.replace({
        name: 'clientPersonalCenterBasic',
        query: this.$route.query,
      }).catch(() => {});
    },
    /** 切换左侧菜单：走路由，刷新后仍停留在当前面板 */
    onMenuChange(menuKey) {
      if (menuKey === this.activeMenu) return;
      this.$router.push({
        path: personalCenterMenuPath(menuKey),
        query: this.$route.query,
      }).catch(() => {});
    },
    /** 进入个人中心：复用 indiv 会话缓存，不重复请求 */
    ensureIndivSession(force = false) {
      return refreshIndivSession({
        force,
        store: this.$store,
      });
    },
    /** 实名认证成功后强制刷新会话 */
    onRealnameSuccess() {
      this.ensureIndivSession(true);
    },
    /** 已实名用户查看实名记录 */
    onRealnameRecord() {
      this.showRealnameRecordDialog = true;
    },
    /** 侧边栏「购买服务 / 续费」 */
    onPurchase() {
      this.openPurchaseDialog(subscriptionState.status);
    },
    /** 侧边栏「免费领取」 */
    onApplyTrial() {
      this.openTrialApplyDialog();
    },
    /** 试用开通成功后刷新 */
    onTrialApplied() {
      this.ensureIndivSession(true);
    },
    /** 当前在订单页时刷新列表（其他面板无 loadOrders，不会执行） */
    refreshOrdersIfVisible() {
      const panel = this.$refs.activePanel;
      if (panel && typeof panel.loadOrders === 'function') {
        panel.loadOrders();
      }
    },
    /** 建单成功后刷新订单列表 */
    onOrderCreated() {
      this.refreshOrdersIfVisible();
    },
    /** 购买支付成功后刷新订单列表 */
    onPurchaseSuccess() {
      this.ensureIndivSession(true);
      this.refreshOrdersIfVisible();
    },
    /** 订单「立即支付」 */
    onOrderPay(order) {
      this.openOrderPayDialog(order);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/view/client/personal-center/layout/Main.vue